<script setup>
/* global axios */
// Email Marketing — relatório/detalhe de uma campanha.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const campaignId = route.params.campaignId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const campaign = ref(null);
const contacts = ref([]);
const loading = ref(true);
const contactsLoading = ref(false);
const statusFilter = ref('');
const actionLoading = ref('');
const msg = ref('');
let timer = null;

const showHistory = ref(null);
const history = ref([]);
const historyLoading = ref(false);

const CAMPAIGN_BADGE = {
  draft: 'bg-n-alpha-2 text-n-slate-11',
  scheduled: 'bg-n-blue-3 text-n-blue-11',
  running: 'bg-n-teal-3 text-n-teal-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-iris-3 text-n-iris-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};
const CONTACT_BADGE = {
  pending: 'bg-n-alpha-2 text-n-slate-11',
  sent: 'bg-n-teal-3 text-n-teal-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  skipped: 'bg-n-amber-3 text-n-amber-11',
};
const CONTACT_STATUSES = ['pending', 'sent', 'failed', 'skipped'];
const fmt = iso => (iso ? new Date(iso).toLocaleString() : '—');
const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString() : '—');

const sent = computed(() => campaign.value?.sent_count || 0);
const total = computed(() => campaign.value?.total_contacts || 0);
const progress = computed(() =>
  total.value ? Math.round((sent.value / total.value) * 100) : 0
);
const openRate = computed(() =>
  sent.value
    ? (((campaign.value?.open_count || 0) / sent.value) * 100).toFixed(1)
    : '0.0'
);
const clickRate = computed(() =>
  sent.value
    ? (((campaign.value?.click_count || 0) / sent.value) * 100).toFixed(1)
    : '0.0'
);
const failRate = computed(() =>
  total.value
    ? (((campaign.value?.failed_count || 0) / total.value) * 100).toFixed(1)
    : '0.0'
);
const pending = computed(() =>
  Math.max(0, total.value - sent.value - (campaign.value?.failed_count || 0))
);

const loadCampaign = async () => {
  try {
    campaign.value = (
      await axios.get(`${base}/email_campaigns/${campaignId}`)
    ).data.data;
  } catch {
    campaign.value = null;
  }
  loading.value = false;
};
const loadContacts = async () => {
  contactsLoading.value = true;
  try {
    const q = statusFilter.value ? `?status=${statusFilter.value}` : '';
    contacts.value =
      (await axios.get(`${base}/email_campaigns/${campaignId}/contacts${q}`))
        .data.data || [];
  } finally {
    contactsLoading.value = false;
  }
};
const setStatusFilter = e => {
  statusFilter.value = e.target.value;
  loadContacts();
};

onMounted(async () => {
  await loadCampaign();
  await loadContacts();
  timer = setInterval(() => {
    if (campaign.value?.status === 'running') loadCampaign();
  }, 10000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

const doAction = async (key, path) => {
  actionLoading.value = key;
  msg.value = '';
  try {
    await axios.post(`${base}/email_campaigns/${campaignId}/${path}`);
    await loadCampaign();
    await loadContacts();
  } catch (e) {
    msg.value = e?.response?.data?.error || t('EMAIL.report.opError');
  } finally {
    actionLoading.value = '';
  }
};
const duplicate = async () => {
  actionLoading.value = 'duplicate';
  try {
    const dup = (
      await axios.post(`${base}/email_campaigns/${campaignId}/duplicate`)
    ).data.data;
    if (dup?.id)
      router.push({
        name: 'kanban_email_report',
        params: { accountId, campaignId: dup.id },
      });
  } catch {
    msg.value = t('EMAIL.report.opError');
  } finally {
    actionLoading.value = '';
  }
};
const resendUnopened = async () => {
  actionLoading.value = 'resend';
  msg.value = '';
  try {
    const dup = (
      await axios.post(`${base}/email_campaigns/${campaignId}/resend_unopened`)
    ).data.data;
    if (dup?.id)
      router.push({
        name: 'kanban_email_report',
        params: { accountId, campaignId: dup.id },
      });
  } catch (e) {
    msg.value = e?.response?.data?.error || t('EMAIL.report.opError');
  } finally {
    actionLoading.value = '';
  }
};
const segment = async filter => {
  actionLoading.value = `seg_${filter}`;
  msg.value = '';
  try {
    const dup = (
      await axios.post(`${base}/email_campaigns/${campaignId}/segment`, {
        filter,
      })
    ).data.data;
    if (dup?.id)
      router.push({
        name: 'kanban_email_report',
        params: { accountId, campaignId: dup.id },
      });
  } catch (e) {
    msg.value = e?.response?.data?.error || t('EMAIL.report.opError');
  } finally {
    actionLoading.value = '';
  }
};

const openHistory = async email => {
  showHistory.value = email;
  historyLoading.value = true;
  try {
    history.value =
      (
        await axios.get(
          `${base}/email_campaigns/contact_history?email=${encodeURIComponent(email)}`
        )
      ).data.data || [];
  } catch {
    history.value = [];
  } finally {
    historyLoading.value = false;
  }
};

const exportCsv = () => {
  const headers = [
    'email',
    'nome',
    'status',
    'enviado_em',
    'aberto_em',
    'clique_em',
  ];
  const rows = contacts.value.map(c => [
    c.email,
    c.name || '',
    c.status,
    fmt(c.sent_at),
    fmt(c.opened_at),
    fmt(c.clicked_at),
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `campanha-${campaignId}-contatos.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div
      v-if="loading"
      class="flex items-center justify-center h-full text-sm text-n-slate-11"
    >
      {{ t('EMAIL.loading') }}
    </div>
    <div
      v-else-if="!campaign"
      class="flex items-center justify-center h-full text-sm text-n-slate-10"
    >
      {{ t('EMAIL.report.notFound') }}
    </div>
    <template v-else>
      <!-- header -->
      <header class="px-6 py-4 border-b border-n-weak bg-n-solid-2">
        <div class="flex items-center gap-3">
          <button
            class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="router.push({ name: 'kanban_email' })"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <div class="flex-1 min-w-0">
            <h1 class="text-heading-1 truncate text-n-slate-12">
              {{ campaign.name }}
            </h1>
            <div class="flex items-center gap-2 mt-0.5">
              <span
                class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                :class="CAMPAIGN_BADGE[campaign.status]"
                >{{ t(`EMAIL.status.${campaign.status}`) }}</span
              >
              <span class="text-xs text-n-slate-10">{{
                t('EMAIL.report.createdAt', {
                  date: fmtDate(campaign.created_at),
                })
              }}</span>
            </div>
          </div>
          <span v-if="msg" class="text-xs text-n-ruby-11">{{ msg }}</span>
          <div class="flex items-center gap-1.5 shrink-0">
            <button
              v-if="campaign.status === 'running'"
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="doAction('pause', 'pause')"
            >
              {{ t('EMAIL.act.pause') }}
            </button>
            <button
              v-if="campaign.status === 'paused'"
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="doAction('resume', 'resume')"
            >
              {{ t('EMAIL.act.resume') }}
            </button>
            <button
              v-if="['draft', 'scheduled'].includes(campaign.status)"
              class="px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110"
              :disabled="!!actionLoading"
              @click="doAction('launch', 'launch')"
            >
              {{ t('EMAIL.report.launch') }}
            </button>
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="duplicate"
            >
              {{ t('EMAIL.act.duplicate') }}
            </button>
          </div>
        </div>

        <!-- progress -->
        <div v-if="total > 0" class="mt-3">
          <div
            class="flex items-center justify-between mb-1 text-xs text-n-slate-10"
          >
            <span>{{
              t('EMAIL.report.progressSent', {
                sent,
                total,
                progress,
              })
            }}</span>
            <span v-if="pending > 0">{{
              t('EMAIL.report.pending', { count: pending })
            }}</span>
          </div>
          <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full transition-all rounded-full bg-n-brand"
              :style="{ width: `${progress}%` }"
            />
          </div>
        </div>
      </header>

      <div class="flex-1 p-6 space-y-6 overflow-y-auto">
        <!-- stats -->
        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.total')
            }}</span>
            <span class="text-2xl font-semibold text-n-slate-12">{{
              total
            }}</span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.sent')
            }}</span>
            <span class="text-2xl font-semibold text-n-teal-11">{{
              sent
            }}</span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.openRate')
            }}</span>
            <span class="text-2xl font-semibold text-n-blue-11"
              >{{ openRate }}%</span
            >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.openCount', {
                count: campaign.open_count || 0,
              })
            }}</span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.clickRate')
            }}</span>
            <span class="text-2xl font-semibold text-n-iris-11"
              >{{ clickRate }}%</span
            >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.clickCount', {
                count: campaign.click_count || 0,
              })
            }}</span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.failed')
            }}</span>
            <span class="text-2xl font-semibold text-n-ruby-11">{{
              campaign.failed_count || 0
            }}</span>
            <span class="text-xs text-n-slate-10">{{ failRate }}%</span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="text-xs text-n-slate-10">{{
              t('EMAIL.report.stats.pending')
            }}</span>
            <span class="text-2xl font-semibold text-n-slate-12">{{
              pending
            }}</span>
          </div>
        </div>

        <!-- post actions -->
        <div
          v-if="['completed', 'paused'].includes(campaign.status) && sent > 0"
          class="p-4 border rounded-lg border-n-weak bg-n-solid-2"
        >
          <h3 class="text-heading-3 mb-3 text-n-slate-12">
            {{ t('EMAIL.report.post.title') }}
          </h3>
          <div class="flex flex-wrap gap-2">
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="resendUnopened"
            >
              {{ t('EMAIL.report.post.resendUnopened') }}
            </button>
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="segment('opened')"
            >
              {{ t('EMAIL.report.post.segOpened') }}
            </button>
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="segment('clicked')"
            >
              {{ t('EMAIL.report.post.segClicked') }}
            </button>
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
              :disabled="!!actionLoading"
              @click="segment('failed')"
            >
              {{ t('EMAIL.report.post.segFailed') }}
            </button>
          </div>
        </div>

        <!-- contacts -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-heading-2 text-n-slate-12">
              {{ t('EMAIL.report.contacts.title') }}
            </h2>
            <div class="flex items-center gap-2">
              <select
                :value="statusFilter"
                class="!w-auto pl-3 pr-9 h-8 text-sm rounded-lg border-0 outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 outline-n-weak focus:outline-n-brand focus:ring-0"
                @change="setStatusFilter"
              >
                <option value="">{{ t('EMAIL.report.contacts.all') }}</option>
                <option v-for="s in CONTACT_STATUSES" :key="s" :value="s">
                  {{ t(`EMAIL.contactStatus.${s}`) }}
                </option>
              </select>
              <button
                v-if="contacts.length"
                class="px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
                @click="exportCsv"
              >
                {{ t('EMAIL.report.contacts.export') }}
              </button>
            </div>
          </div>

          <div v-if="contactsLoading" class="text-sm text-n-slate-10">
            {{ t('EMAIL.loading') }}
          </div>
          <p
            v-else-if="!contacts.length"
            class="py-8 text-sm text-center text-n-slate-10"
          >
            {{ t('EMAIL.report.contacts.none') }}
          </p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-left border-b border-n-weak text-n-slate-10">
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.contacts.colEmail') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.contacts.colName') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.contacts.colStatus') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.contacts.colSent') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.contacts.colOpened') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.contacts.colClicked') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in contacts"
                :key="c.id"
                class="border-b border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2">
                  <button
                    class="font-mono text-xs text-n-brand hover:underline"
                    @click="openHistory(c.email)"
                  >
                    {{ c.email }}
                  </button>
                </td>
                <td class="px-3 py-2 text-n-slate-11">{{ c.name || '—' }}</td>
                <td class="px-3 py-2">
                  <span
                    class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                    :class="CONTACT_BADGE[c.status]"
                    >{{ t(`EMAIL.contactStatus.${c.status}`) }}</span
                  >
                </td>
                <td class="px-3 py-2 text-xs text-n-slate-10">
                  {{ fmt(c.sent_at) }}
                </td>
                <td
                  class="px-3 py-2 text-xs"
                  :class="c.opened_at ? 'text-n-blue-11' : 'text-n-slate-10'"
                >
                  {{ fmt(c.opened_at) }}
                </td>
                <td
                  class="px-3 py-2 text-xs"
                  :class="c.clicked_at ? 'text-n-iris-11' : 'text-n-slate-10'"
                >
                  {{ fmt(c.clicked_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- history modal -->
    <div
      v-if="showHistory"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
    >
      <div
        class="w-full max-w-xl overflow-hidden shadow-md rounded-xl bg-n-solid-2"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('EMAIL.report.history.title', { email: showHistory }) }}
          </h3>
          <button
            class="p-1 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="showHistory = null"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="px-6 py-5 overflow-auto max-h-[60vh]">
          <p
            v-if="historyLoading"
            class="py-4 text-sm text-center text-n-slate-10"
          >
            {{ t('EMAIL.loading') }}
          </p>
          <p
            v-else-if="!history.length"
            class="py-4 text-sm text-center text-n-slate-10"
          >
            {{ t('EMAIL.report.history.none') }}
          </p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-left border-b border-n-weak text-n-slate-10">
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.history.colCampaign') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.history.colStatus') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('EMAIL.report.history.colSent') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="h in history"
                :key="h.id"
                class="border-b border-n-weak"
              >
                <td class="px-3 py-2 text-n-slate-12">
                  {{ h.campaign ? h.campaign.name : '—' }}
                </td>
                <td class="px-3 py-2">
                  <span
                    class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                    :class="CONTACT_BADGE[h.status]"
                    >{{ t(`EMAIL.contactStatus.${h.status}`) }}</span
                  >
                </td>
                <td class="px-3 py-2 text-xs text-n-slate-10">
                  {{ fmt(h.sent_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="flex justify-end px-6 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
            @click="showHistory = null"
          >
            {{ t('EMAIL.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
